/**
 * Copy for the public site. Everything here describes what recoup does today; anything not
 * built yet says so ("coming"). Vendor facts are quoted only where checked against the
 * vendor's own published SLA (2026-10-19); otherwise a vendor page speaks about the contract.
 */

export interface Section {
  heading: string;
  text?: string;
  bullets?: string[];
}

export interface Entry {
  slug: string;
  title: string;
  /** One line for menus and cards. */
  summary: string;
  /** The page's opening paragraph. */
  lead: string;
  sections: Section[];
  /** Shown as a label when the thing isn't built yet. */
  coming?: boolean;
}

export interface Vendor {
  slug: string;
  name: string;
  category: string;
  /** The vendor's public status page: one of the places recoup watches (each checked to respond). */
  status: string;
  /** Checked against the vendor's published SLA; absent when terms vary by product and plan. */
  published?: {
    source: string;
    /** When we read the published terms. */
    checked: string;
    promise: string;
    credits: string[];
    deadline: string;
    howToClaim: string;
    note?: string;
  };
}

export const PRODUCTS: Entry[] = [
  {
    slug: "contract-reader",
    title: "Contract reader",
    summary: "Finds what each vendor promised you, and what a miss is worth.",
    lead: "Upload a vendor contract as a PDF, Word file or text. recoup lists what the vendor promised, what you get back when they miss, and the last day to ask. It quotes the contract word for word, so you can check it.",
    sections: [
      {
        heading: "What it finds",
        bullets: [
          "How much uptime the vendor promised",
          "How fast they promised to answer support requests",
          "What you get back for each miss",
          "How and when to ask for it",
          "When the contract renews",
        ],
      },
      {
        heading: "You check it first",
        text: "Nothing counts until you approve it. Each line shows the words it came from. If the contract is unclear, recoup asks you a simple question.",
      },
      {
        heading: "When a contract pays nothing",
        text: "Some contracts promise uptime but give nothing back when it's missed. recoup tells you, and still records each miss for your next renewal talk.",
      },
    ],
  },
  {
    slug: "vendor-watch",
    title: "Vendor monitoring",
    summary: "Checks each vendor against what your contract promised.",
    lead: "recoup keeps an eye on every vendor and writes down every outage, with the date and time. Each month, it checks the vendor against what your contract promised.",
    sections: [
      {
        heading: "How it knows",
        bullets: [
          "The vendor's public status page",
          "Its own checks on the vendor's service",
          "Outage emails the vendor sends you, forwarded to recoup",
          "Google Cloud's service health, if you use Google Cloud",
        ],
      },
      {
        heading: "Quick to set up",
        text: "Add a vendor and recoup suggests how to monitor it. You confirm before it starts.",
      },
      {
        heading: "A record over time",
        text: "Each vendor gets a month-by-month record, so you can see who lets you down most, and what it cost.",
      },
    ],
  },
  {
    slug: "claims",
    title: "Claims",
    summary: "Writes, sends and follows each claim until you're paid.",
    lead: "When a vendor misses its promise, recoup works out what you're owed, shows how, and writes the claim for you, before the deadline.",
    sections: [
      {
        heading: "Every number shown",
        text: "Each claim shows the fee, the uptime, and how the amount was worked out. Anyone can check it, including the vendor.",
      },
      {
        heading: "You stay in charge",
        bullets: [
          "You read each claim before it goes, or let small ones send on their own",
          "Proof of each outage goes with the claim, as a PDF and a spreadsheet",
          "recoup reads the vendor's reply and tells you what it means",
          "If the vendor pushes back, recoup writes your answer from the contract",
        ],
      },
      {
        heading: "Until you're paid",
        text: "recoup follows each claim until the money shows up on your bill, and reminds you if the vendor goes quiet.",
      },
    ],
  },
  {
    slug: "deadline-alerts",
    title: "Deadline alerts",
    summary: "Tells you before a deadline passes.",
    lead: "Some contracts give you only 15 days after the month ends to ask. Some want you to tell them during the outage. recoup knows each deadline and puts the next one at the top of your list.",
    sections: [
      {
        heading: "What it reminds you about",
        bullets: [
          "The last day to claim each month",
          "Outages you need to report right away",
          "Contract renewals, so you can negotiate in time",
          "Claims the vendor hasn't answered after 30 days",
        ],
      },
    ],
  },
  {
    slug: "scorecards-and-renewals",
    title: "Scorecards and renewals",
    summary: "Each vendor's track record, ready for your renewal talk.",
    lead: "recoup keeps a record of every month a vendor missed, with proof, even when the contract paid nothing. When the contract comes up for renewal, that record helps you get a better deal.",
    sections: [
      {
        heading: "What you bring to the talk",
        bullets: [
          "Every month the vendor missed, with proof",
          "What you claimed, what was paid, and what's still open",
          "Which vendors give nothing back at all",
        ],
      },
      {
        heading: "Across many companies",
        text: "A single view across several companies is coming. Today, each company sees its own.",
      },
    ],
  },
];

export const SOLUTIONS: Entry[] = [
  {
    slug: "sla-credit-recovery",
    title: "Get back what you're owed",
    summary: "Claim the money your contracts already promise.",
    lead: "Many vendor contracts promise money back when the vendor lets you down. To get it, someone has to notice, measure, work out the amount and ask. recoup does all four, for every vendor, every month.",
    sections: [
      {
        heading: "How you get paid",
        text: "It's usually a share of that month's fee, taken off a later bill. One claim is small. Across many vendors and months, it adds up.",
      },
      {
        heading: "What you need",
        text: "Just your vendor contracts. recoup does the rest, and you approve what goes out.",
      },
    ],
  },
  {
    slug: "never-miss-a-deadline",
    title: "Never miss a deadline",
    summary: "Short deadlines, tracked for you.",
    lead: "A lot of money is lost because nobody asked in time. Atlassian gives you 15 days after the month ends. AWS gives you about two months. recoup knows each deadline and claims before it.",
    sections: [
      {
        heading: "When you must speak up during the outage",
        text: "Some contracts only pay if you report the outage right away. recoup tells you as soon as it sees one.",
      },
    ],
  },
  {
    slug: "vendor-renewals",
    title: "Better renewal deals",
    summary: "Go into your renewal talk with the vendor's track record.",
    lead: 'Renewals are where the real savings are. A dated record of every month the vendor missed turns "we\'ve had some issues" into hard facts.',
    sections: [
      {
        heading: "In good time",
        text: "recoup reminds you before each renewal deadline, with that vendor's record ready.",
      },
    ],
  },
  {
    slug: "portfolio-value-creation",
    title: "Savings across a portfolio",
    summary: "An easy win for every company you own.",
    lead: "For private equity firms: every company you own pays software vendors that miss their promises. Introduce recoup once, and each company will get a free audit, and recoup then monitors its vendors every month.",
    sections: [
      {
        heading: "How it works",
        bullets: [
          "You introduce recoup to each company's finance lead",
          "Each company will get its own free audit and its own account",
          "Each company sees what it was owed, and what came back",
        ],
      },
      {
        heading: "One view of everything",
        text: "A single view across all your companies is coming.",
      },
    ],
  },
  {
    slug: "know-what-you-are-owed",
    title: "Know who owes you nothing",
    summary: "See which contracts pay you back, and which don't.",
    lead: "Many contracts promise uptime but give nothing back when it's missed. recoup sorts your vendors into three groups: pays you back, pays nothing, and no contract yet. Then you know where to push at renewal.",
    sections: [],
  },
];

export const INDUSTRIES: Entry[] = [
  {
    slug: "software-and-technology",
    title: "Software and technology",
    summary: "Companies that run on cloud and developer tools.",
    lead: "Software companies rely on cloud hosting, developer tools and messaging services. When those vendors go down, so do you. Your contract may owe you money for it.",
    sections: [
      {
        heading: "Vendors we often see",
        bullets: [
          "Cloud hosting and databases",
          "Code hosting",
          "Monitoring tools",
          "Email and text messaging services",
        ],
      },
    ],
  },
  {
    slug: "financial-services",
    title: "Financial services",
    summary: "Firms that already keep a close eye on vendors.",
    lead: "Financial firms already track their vendors closely. recoup turns that tracking into money back, plus a dated record of how each vendor did.",
    sections: [
      {
        heading: "Fits how you already work",
        text: "Every number comes with the contract's words and proof, so each claim holds up to review.",
      },
    ],
  },
  {
    slug: "healthcare",
    title: "Healthcare",
    summary: "Care providers and health tech companies that run on software.",
    lead: "Healthcare teams rely on software for scheduling, billing and messaging. When those vendors miss their promises, recoup claims what your contracts owe you.",
    sections: [
      {
        heading: "What recoup needs",
        text: "Only your vendor contracts and the vendors' public status pages. recoup never needs patient data or access to your systems.",
      },
    ],
  },
  {
    slug: "retail-and-ecommerce",
    title: "Retail and ecommerce",
    summary: "Stores, payments and support tools that can't go down.",
    lead: "When your store, payments or support tool goes down, you lose sales that day. Your contract often owes you money for it. recoup makes sure you ask.",
    sections: [],
  },
  {
    slug: "manufacturing-and-distribution",
    title: "Manufacturing and distribution",
    summary: "Business software that runs in the cloud.",
    lead: "More manufacturers now run their planning, sales and inventory software in the cloud. Those contracts have uptime promises too, and the people who own them rarely monitor them for outages.",
    sections: [],
  },
  {
    slug: "professional-services",
    title: "Professional services",
    summary: "Firms whose work runs on shared software.",
    lead: "Agencies, consultancies and law firms run on shared documents and project tools. recoup reads those contracts and claims what each missed month is worth.",
    sections: [],
  },
];

export const CUSTOMERS: Entry[] = [
  {
    slug: "finance-leaders",
    title: "Finance",
    summary: "A recovery audit for your software contracts.",
    lead: "This is money owed to your company that never shows up in your forecast. recoup gets it back, shows every number, and tells you what came back and what's still open.",
    sections: [
      {
        heading: "What you get",
        bullets: [
          "A running total of money recovered",
          "Every claim with its math and proof",
          "Each payment recorded when it lands, and chased when it doesn't",
        ],
      },
    ],
  },
  {
    slug: "procurement",
    title: "Procurement",
    summary: "Hold vendors to what they signed.",
    lead: "You negotiated the promise. recoup holds the vendor to it every month, and gives you their track record before it's time to renew.",
    sections: [
      {
        heading: "What you get",
        bullets: [
          "Every contract's promises, in the contract's own words",
          "Reminders before each renewal",
          "Your vendors sorted by who pays you back",
        ],
      },
    ],
  },
  {
    slug: "it-and-operations",
    title: "IT and operations",
    summary: "Turn the outages you live through into money back.",
    lead: "You feel every vendor outage first. recoup writes each one down against the contract, so the outage your team fought through turns into money back.",
    sections: [
      {
        heading: "Little to set up",
        text: "Add a vendor and recoup suggests how to monitor it. Forward the vendor's outage emails to recoup, and they count as proof.",
      },
    ],
  },
  {
    slug: "pe-operating-partners",
    title: "Private equity firms",
    summary: "Introduce recoup once, and every company is covered.",
    lead: "Introduce recoup once, and each company you own will get a free audit and its own account. The money their vendors owe them gets claimed.",
    sections: [
      {
        heading: "How it works",
        bullets: [
          "A free audit at each company: up to 5 contracts, the last 12 months",
          "Each company's data stays private to that company",
          "A single view across all your companies is coming",
        ],
      },
    ],
  },
  {
    slug: "small-companies",
    title: "Small companies",
    summary: "Up to 5 vendors for $20 a month, plus 15% of what comes back.",
    lead: "The same price as everyone, at the smallest size: $20 a month covers up to 5 vendors. A free audit shows what you were owed before you pay anything.",
    sections: [],
  },
];

export const VENDORS: Vendor[] = [
  {
    slug: "aws",
    name: "Amazon Web Services (EC2)",
    category: "Cloud infrastructure",
    status: "https://health.aws.amazon.com/health/status",
    published: {
      checked: "2026-10-19",
      source: "https://aws.amazon.com/compute/sla/",
      promise:
        "99.99% monthly uptime per region when running across two or more Availability Zones; 99.5% for a single instance",
      credits: ["Below 99.99% (region): 10%", "Below 99.0%: 30%", "Below 95.0%: 100%"],
      deadline: "By the end of the second billing cycle after the incident",
      howToClaim:
        "A case in the AWS Support Center, with the claim wording AWS specifies in the subject line",
      note: "The money comes off future EC2 bills, and only if it is more than $1 that month.",
    },
  },
  {
    slug: "atlassian",
    name: "Atlassian Cloud (Jira, Confluence)",
    category: "Collaboration",
    status: "https://status.atlassian.com",
    published: {
      checked: "2026-10-19",
      source: "https://www.atlassian.com/legal/sla",
      promise: "99.9% monthly uptime on Premium, 99.95% on Enterprise",
      credits: [
        "Premium: 10% below 99.9%, 25% below 99.0%, 50% below 95.0%",
        "Enterprise: 5% below 99.95%, then 10%, 25% and 50%",
      ],
      deadline: "Within 15 days after the end of the month",
      howToClaim: "A ticket at support.atlassian.com, with every field completed",
      note: "Atlassian's own records decide whether it missed, so your claim needs to match them.",
    },
  },
  {
    slug: "slack",
    name: "Slack",
    category: "Collaboration",
    status: "https://slack-status.com",
    published: {
      checked: "2026-10-19",
      source: "https://slack.com/terms/service-level-agreement",
      promise:
        "Commercially reasonable efforts to keep the service available, except planned downtime and events outside its control",
      credits: [
        "No credit table on the public page: credits, where they exist, are in your order form",
      ],
      deadline: "As your order form sets out",
      howToClaim: "As your order form sets out",
    },
  },
  {
    slug: "google-cloud",
    name: "Google Cloud (Compute Engine)",
    category: "Cloud infrastructure",
    status: "https://status.cloud.google.com",
    published: {
      source: "https://cloud.google.com/compute/sla",
      checked: "2026-10-24",
      promise:
        "99.99% monthly uptime for instances across multiple zones (Premium tier); 99.9% for most single instances",
      credits: ["Below 99.99%: 10%", "Below 99.0%: 25%", "Below 95.0%: 100%"],
      deadline: "Within 60 days of becoming eligible for the credit",
      howToClaim:
        "Notify Google technical support, with log files showing when the downtime happened",
      note: "The money comes off future bills, up to that month's bill. Without your logs, you get nothing.",
    },
  },
  {
    slug: "microsoft-azure",
    name: "Microsoft Azure",
    category: "Cloud infrastructure",
    status: "https://azure.status.microsoft",
  },
  {
    slug: "digitalocean",
    name: "DigitalOcean (Droplets)",
    category: "Cloud infrastructure",
    status: "https://status.digitalocean.com",
    published: {
      source: "https://www.digitalocean.com/sla/cpu-droplets",
      checked: "2026-10-24",
      promise: "99.99% monthly uptime for each Droplet, measured one Droplet at a time",
      credits: ["Below 99.99%: 100% of that Droplet's charges"],
      deadline: "Within two billing cycles of the month the downtime happened",
      howToClaim:
        "Email success@digitalocean.com with your account email, the affected Droplets and the outage times",
      note: "The money only comes off future Droplet bills.",
    },
  },
  {
    slug: "salesforce",
    name: "Salesforce",
    category: "CRM and sales",
    status: "https://status.salesforce.com",
  },
  {
    slug: "hubspot",
    name: "HubSpot",
    category: "CRM and sales",
    status: "https://status.hubspot.com",
  },
  {
    slug: "zendesk",
    name: "Zendesk",
    category: "Customer support",
    status: "https://status.zendesk.com",
  },
  {
    slug: "twilio",
    name: "Twilio (APIs)",
    category: "Communications",
    status: "https://status.twilio.com",
    published: {
      source: "https://www.twilio.com/en-us/legal/service-level-agreement/twilio-apis",
      checked: "2026-10-24",
      promise: "99.95% monthly API availability; 99.99% with Twilio Enterprise Edition",
      credits: ["Below the threshold: 10% of that month's fees for the affected APIs"],
      deadline: "Within 30 days from the last day of the month",
      howToClaim:
        "A support ticket at help.twilio.com titled “SLA Claim”, with the outage times and your documentation",
      note: "Outages under five minutes don't count. The money only comes off future Twilio bills.",
    },
  },
  {
    slug: "github",
    name: "GitHub",
    category: "Developer tools",
    status: "https://www.githubstatus.com",
  },
  {
    slug: "linear",
    name: "Linear",
    category: "Developer tools",
    status: "https://linearstatus.com",
  },
  { slug: "box", name: "Box", category: "Files and content", status: "https://status.box.com" },
  {
    slug: "okta",
    name: "Okta (Auth0 platform)",
    category: "Identity",
    status: "https://status.okta.com",
    published: {
      source:
        "https://okta.com/sites/default/files/2022-02/ServiceLevelAgreement-for-Auth0-Platform-02-7-22.pdf",
      checked: "2026-10-24",
      promise: "99.99% average monthly availability for the Auth0 platform's core services",
      credits: [
        "Below 99.99%: 5%",
        "Below 99.9%: 10%",
        "Below 99.0%: 20%",
        "Below 95%: 50% of the monthly fee",
      ],
      deadline: "In writing, within five business days of the event",
      howToClaim: "Contact Okta in writing",
      note: "From Okta's published Auth0 terms (February 2022). Five business days is the shortest deadline we've seen. Miss it and the money is gone.",
    },
  },
  { slug: "zoom", name: "Zoom", category: "Communications", status: "https://status.zoom.us" },
  {
    slug: "docusign",
    name: "Docusign",
    category: "Files and content",
    status: "https://status.docusign.com",
  },
];

/** The one price after the free audit; the server's billing uses the same numbers. */
export const PRICE = { perVendor: 4, minVendors: 5, successFee: 15, auditContracts: 5 } as const;

export const FAIR_USE = [
  "One free audit per company, using a work email",
  "You pay for at least 5 vendors, so the smallest bill is $20 a month",
  "Each vendor counts once, however many files you upload for it",
  "A contract over about 100 pages counts as two vendors",
  "The 15% is only taken from money recoup claimed, and only once it's paid",
];
